import type { QueryClient, QueryKey } from '@tanstack/react-query';

// SSE 데이터 변경 이벤트 → 무효화 대상(순수 함수). useSSE.ts가 쓴다.
// 이벤트와 무관한 키(스토리보드·회의록 등)는 건드리지 않고, 접두사 무효화가 레이아웃 전역 쿼리까지 번지지 않게 exact를 쓴다.

/** 데이터 동기화 토스트 최소 간격 — 아침 정리 시간대처럼 수정이 연달아 와도 한 번만 알린다 */
export const SYNC_TOAST_THROTTLE_MS = 5_000;

/** useSSE가 구독하는 데이터 변경 이벤트. 서버 이벤트 목록은 context/ApiEndpoints.md §16 */
export const SSE_DATA_CHANGE_EVENTS = [
  'project:created',
  'project:updated',
  'project:deleted',
  'activity:created',
  'activity:updated',
  'activity:deleted',
  'issue-report:created',
  'issue-report:updated',
  'issue-report:deleted',
] as const;

export interface InvalidationTarget {
  queryKey: QueryKey;
  /** true면 접두사가 아니라 정확히 이 키만 (예: 레이아웃 배지 ['dashboard','provisional','my-count']는 제외) */
  exact?: boolean;
}

function toId(value: unknown): number | null {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * 이벤트별 무효화 쿼리 키. 알 수 없는 이벤트는 빈 배열.
 * - project:* → 프로젝트 목록(`['projects']` 접두사: 목록·모달 목록), 해당 프로젝트 상세
 * - activity:* → 간트 업무, 해당 프로젝트 상세(업무 목록), 대시보드 가등록 목록(exact — 레이아웃 배지는 30초 폴링에 맡긴다)
 * - issue-report:* → 이슈 목록, 해당 이슈 상세(상세 페이지 키는 useParams 문자열)
 */
export function sseInvalidationTargets(event: string, data: Record<string, unknown>): InvalidationTarget[] {
  const projectId = toId(data.projectId);
  const reportId = toId(data.reportId);
  const family = event.split(':')[0];
  switch (family) {
    case 'project': {
      const targets: InvalidationTarget[] = [{ queryKey: ['projects'] }];
      if (projectId) targets.push({ queryKey: ['project', projectId] });
      return targets;
    }
    case 'activity': {
      const targets: InvalidationTarget[] = [{ queryKey: ['gantt', 'tasks'] }];
      if (projectId) targets.push({ queryKey: ['project', projectId] });
      targets.push({ queryKey: ['dashboard', 'provisional'], exact: true });
      return targets;
    }
    case 'issue-report': {
      const targets: InvalidationTarget[] = [{ queryKey: ['issue-reports'] }];
      if (reportId) targets.push({ queryKey: ['issue-report', String(reportId)] });
      return targets;
    }
    default:
      return [];
  }
}

/** 대상 중 현재 화면에 떠 있는(구독자가 있는) 쿼리가 하나라도 있는가 — 토스트는 이때만 띄운다 */
export function hasActiveQuery(queryClient: QueryClient, targets: readonly InvalidationTarget[]): boolean {
  const cache = queryClient.getQueryCache();
  return targets.some((t) => cache.findAll({ queryKey: t.queryKey, exact: t.exact, type: 'active' }).length > 0);
}
